import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { CalendarBlank, ChevronDown } from "@piiqtechnologies/svg-icons";
import { PeekBox, PeekButton, PeekMenu, PeekMenuItem, PeekStack, PeekTooltip, PeekTypography, useTheme, } from "@piiqtechnologies/ui-components";
import { formatDateFromUTC } from "@piiqtechnologies/ui-lib";
import { latestReportId } from "@hooks/useSelectedReport";
import messages from "./messages.i18n";
const formatReportDate = (completedAt) => {
    if (!completedAt)
        return "";
    const parsed = new Date(completedAt);
    if (Number.isNaN(parsed.getTime()))
        return "";
    return formatDateFromUTC(parsed, "MMMM d, yyyy");
};
export const ReportDateSelector = ({ reports, selectedReportId, onSelect, readOnly, }) => {
    const intl = useIntl();
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null);
    const completedReports = useMemo(() => reports.filter((report) => report.status === "completed"), [reports]);
    const latestId = latestReportId(completedReports);
    const selected = useMemo(() => { var _a; return (_a = completedReports.find((report) => report.reportId === selectedReportId)) !== null && _a !== void 0 ? _a : completedReports[0]; }, [completedReports, selectedReportId]);
    const handleSelect = (reportId) => {
        setAnchorEl(null);
        if (reportId !== selectedReportId)
            onSelect(reportId);
    };
    const isLatest = Boolean(selected) && (selected === null || selected === void 0 ? void 0 : selected.reportId) === latestId;
    // Three states on one control: the newest report; an older one from the template still
    // running; or one from a template that no longer runs, which is why the date on screen will
    // never move again. A community with no report yet has nothing to pick at all.
    const isInactive = Boolean(selected) && !isLatest && !(selected === null || selected === void 0 ? void 0 : selected.isActive);
    const stateColor = !selected
        ? theme.palette.grey[300]
        : isLatest
            ? theme.palette.teal[600]
            : isInactive
                ? theme.palette.grey[300]
                : theme.palette.warning.main;
    const label = (React.createElement(React.Fragment, null,
        React.createElement(PeekBox, { component: "span", sx: { color: stateColor, fontStyle: "italic", mr: selected ? 0.5 : 0 } }, selected ? (React.createElement(React.Fragment, null,
            intl.formatMessage(isLatest ? messages.latest : messages.past),
            " \u00B7")) : (intl.formatMessage(messages.noReportsYet))),
        selected && formatReportDate(selected.completedAt)));
    const calendarIcon = React.createElement(CalendarBlank, { width: 16, height: 16, style: { color: stateColor } });
    if (readOnly) {
        return (React.createElement(PeekTooltip, { title: intl.formatMessage(messages.readOnlyTooltip), placement: "bottom" },
            React.createElement(PeekStack, { direction: "row", alignItems: "center", gap: 1, tabIndex: 0, "aria-label": intl.formatMessage(messages.reportingDataFrom), "data-testid": "report-date-read-only", sx: {
                    height: 40,
                    px: 1,
                    borderRadius: 1,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    backgroundColor: "grey.100",
                    opacity: "0.80",
                } },
                calendarIcon,
                React.createElement(PeekTypography, { variant: "body1" }, label))));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(PeekButton, { variant: "outlined", color: "info", size: "large", disabled: !selected, onClick: (event) => setAnchorEl(event.currentTarget), "aria-haspopup": "listbox", "aria-expanded": Boolean(anchorEl), "aria-label": intl.formatMessage(messages.reportingDataFrom), startIcon: calendarIcon, endIcon: React.createElement(ChevronDown, { width: 14, height: 14 }), sx: {
                whiteSpace: "nowrap",
                "&.Mui-disabled": { color: "text.primary" },
                "& .MuiButton-endIcon": { ml: 1.5 },
            } }, label),
        React.createElement(PeekMenu, { anchorEl: anchorEl, open: Boolean(anchorEl) && Boolean(selected), onClose: () => setAnchorEl(null), anchorOrigin: { vertical: "bottom", horizontal: "right" }, transformOrigin: { vertical: "top", horizontal: "right" }, MenuListProps: { "aria-label": intl.formatMessage(messages.reportingDataFrom), role: "listbox" } }, completedReports.map((report) => {
            const isSelected = report.reportId === (selected === null || selected === void 0 ? void 0 : selected.reportId);
            return (React.createElement(PeekMenuItem, { key: report.reportId, selected: isSelected, hoverBgColor: theme.palette.teal[100], role: "option", "aria-selected": isSelected, onClick: () => handleSelect(report.reportId) },
                React.createElement(PeekStack, { direction: "row", alignItems: "center", gap: 0.5, sx: { width: "100%" } },
                    report.reportId === latestId ? (React.createElement(PeekTypography, { variant: "body1", fontStyle: "italic", color: theme.palette.teal[600] },
                        intl.formatMessage(messages.latest),
                        " \u00B7")) : (!report.isActive && (React.createElement(PeekTypography, { variant: "body1", fontStyle: "italic", color: theme.palette.grey[300] },
                        intl.formatMessage(messages.inactive),
                        " \u00B7"))),
                    React.createElement(PeekTypography, { variant: "body1" }, formatReportDate(report.completedAt)))));
        }))));
};
export default ReportDateSelector;
